
import Vue from 'vue';
import { auth, db } from '@/firebase';
import { mapActions, mapState } from 'vuex';
import { RunnitNodeDef, RunnitNodeRun, RunnitNodeRunResult } from '@run-diffusion/shared';
import _shuffle from 'lodash/shuffle';
import pluralize from 'pluralize';
import ImageGallery from '@/components/ImageGallery/ImageGallery.vue';
import LibraryFilterPanel from '@/components/ImageGallery/LibraryFilterPanel.vue';
import LibrarySearchField from '@/components/ImageGallery/LibrarySearchField.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import RunnitImageInfoCarouselDialog from '@/views/Runnits/RunnitImageInfoCarouselDialog.vue';
import {
	SELECTED_IMAGE_MODE,
	SELECTED_COLUMNS_MAP,
	SELECTED_IMAGE_MODE_MENU_ITEMS,
	NODE_RUN_RESULT_SELECTION_MODE,
	RUNNITS_OWNER_SELECTION,
} from '@/views/Runnits/constants';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import GreyButton from '@/components/base/GreyButton.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import RefreshListButton from '@/components/Filters/RefreshListButton.vue';
import BaseGreyToggleButton from '@/components/base/BaseGreyToggleButton.vue';
import { IMAGE_GALLERY_DIALOG_NAV } from '@/components/ImageGallery/utils';
import {
	parsePermalinkTargetKey,
	permalinkTargetKey,
} from '@/components/GlobalSearch/searchRouting';
import { TeamUserMixin } from '@/mixins';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import SliderSelect from '@/components/base/SliderSelect.vue';
import {
	setRunnitsSelectedColumnsVal,
	setRunnitsSelectedImageModeVal,
	RUNNIT_LIBRARY_FILTER_LOCATION,
} from '@/utils';
import {
	AppliedLibraryFilters,
	buildLibraryFilterQuery,
	countAppliedLibraryFilters,
	createEmptyAppliedLibraryFilters,
	hasAnyAppliedLibraryFilter,
	readAppliedLibraryFiltersFromQuery,
} from '@/utils/libraryFilterFacets';
import {
	fetchLibraryTeamOptions,
	isActiveTeamLibraryEligible,
	LibraryTeamOption,
} from '@/utils/libraryApi';
import {
	LIBRARY_BULK_RESULT_TYPES,
	canDeleteLibraryNodeRuns,
} from '@/utils/libraryBulkSelection';
import { SNACKBAR_STATUS } from '@/constants/constants';

const LIBRARY_API_BASE_URL: string =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

export default Vue.extend({
	name: 'RunnitLibraryPage',
	metaInfo: {
		title: 'Library',
	},
	mixins: [
		RunnitBulkActionsMixin,
		TeamUserMixin,
	],
	data () {
		return {
			RUNNIT_LIBRARY_FILTER_LOCATION: Object.freeze(RUNNIT_LIBRARY_FILTER_LOCATION),
			SELECTED_IMAGE_MODE,
			IMAGE_GALLERY_DIALOG_NAV,
			selectedImageModeMenuItems: SELECTED_IMAGE_MODE_MENU_ITEMS,
			loadingFeaturedNodeRuns: false,
			// Gates the gallery's first render along with the featured load. The
			// gallery resolves its stored scope once, in its own created hook, and can
			// only restore a stored TEAM if TEAM is already among availableFilters —
			// so mounting it before /me answers silently downgraded a restored TEAM to
			// ALL. Starts true because both loads are kicked off from created().
			loadingLibraryTeamOptions: true,
			featuredNodeRuns: [] as RunnitNodeRun[],
			currentNavTab: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS,
			imageInfoCarouselConfig: {
				dialogOpen: false,
				nodeRun: null as RunnitNodeRun | null,
				nodeRunResult: null as RunnitNodeRunResult | null,
			},
			// A run opened via the ?nodeRunId=&resultUuid= permalink (Global
			// Search primary destination / shareable link). While set, the
			// carousel is constrained to just this run and its result filter is
			// permissive (a permalinked result usually isn't `featured`).
			permalinkNodeRun: null as RunnitNodeRun | null,
			selectedScope: 'ALL',    // ALL | FAVORITES | TEAM, lifted from ImageGallery's on-filter-change (decision 7)
			filtersExpanded: false,  // toggled by the "Filter" button; controls whether the panel shows
			selectingAllResults: false,
			libraryTeamOptions: [] as LibraryTeamOption[],
			// Applied snapshot — the only thing that drives the gallery and the URL
			// query. Updated solely from <library-filter-panel>'s `apply` event.
			appliedFilters: createEmptyAppliedLibraryFilters(),
		};
	},
	computed: {
		...mapState([
			'user',
			'runnitState',
			'publicRunnitNodeDefTagsMap',
			'publicRunnitNodeDefsMap',
			'companyRunnitNodeDefsMap',
		]),
		// Shared by the confirm dialog's title and its action button, so they can't drift apart
		deleteGenerationsText (): string {
			return `Delete ${this.selectedImagesCount} ${pluralize('Generation', this.selectedImagesCount)}`;
		},
		navToggleOptions () {
			return [
				{ value: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS, icon: 'mdi-image-multiple-outline', label: 'Library' },
				{ value: IMAGE_GALLERY_DIALOG_NAV.UPLOADS, icon: 'mdi-cloud-upload-outline', label: 'Uploads' },
			];
		},
		selectedImageMode (): string {
			return this.runnitState?.selectedImageMode;
		},
		selectedColumns (): number {
			return this.runnitState?.selectedColumns;
		},
		columnItems () {
			return Object.keys(SELECTED_COLUMNS_MAP).map(key => ({
				value: Number(key),
				label: SELECTED_COLUMNS_MAP[key],
			}));
		},
		availableFilters () {
			const defaultFilters = ['ALL', 'FAVORITES'];
			// The gallery's TEAM tab reads the ACTIVE account's team, so it asks the
			// narrower question. hasEligibleLibraryTeams is the panel's question (any
			// eligible membership) and offered TEAM under a Personal account, where
			// the read would silently answer for the last team the caller acted as.
			if (this.activeTeamLibraryEligible) {
				defaultFilters.push('TEAM');
			}
			return defaultFilters;
		},
		// Any eligible membership: drives the filter panel's Scope control, which
		// names the teams it reads explicitly.
		hasEligibleLibraryTeams (): boolean {
			return this.libraryTeamOptions.some((option) => !option.disabled);
		},
		// The active account's team specifically: drives the gallery's TEAM tab.
		activeTeamLibraryEligible (): boolean {
			return isActiveTeamLibraryEligible({
				teamOptions: this.libraryTeamOptions,
				activeTeamId: this.team?.id || '',
				actingAsTeam: this.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM,
			});
		},
		// Owner (filter-by-user) options. TODO: source the active team's members
		// (userId + email/name); empty keeps the Owner picker hidden for now.
		libraryMemberOptions (): { value: string; text: string }[] {
			return [];
		},
		// Toolbar shows only on the GENERATIONS tab AND when the scope is a
		// facet-supported one (ALL or TEAM). FAVORITES bypasses facet mode
		// (decision 7), so the toolbar is hidden there to avoid dead controls.
		showFacetToolbar (): boolean {
			return this.currentNavTab === IMAGE_GALLERY_DIALOG_NAV.GENERATIONS
				&& (this.selectedScope === 'ALL' || this.selectedScope === 'TEAM');
		},
		// Derived from the APPLIED snapshot (not the in-progress draft): drives the
		// URL query and Select All. The "Filter" button lights from
		// appliedFilterCount, which leaves the search term out.
		activeFilterQuery (): Record<string, string> {
			return buildLibraryFilterQuery(this.appliedFilters);
		},
		hasActiveFilters (): boolean {
			// Not derived from activeFilterQuery: the owner and board filters are
			// deliberately kept out of the URL (see queryKey in LIBRARY_FILTER_FIELDS)
			// but still count toward the "filters active" state.
			return hasAnyAppliedLibraryFilter(this.appliedFilters);
		},
		// The permalink the URL currently names, as one value so a watcher fires
		// on a change to EITHER half. A run holds several results, so navigating
		// between two results of the same run moves only `resultUuid`.
		permalinkTarget (): string {
			return permalinkTargetKey(this.$route.query);
		},
		// Permalinked runs get a permissive result filter (their results usually
		// aren't `featured`); the featured browse path keeps the featured filter.
		carouselResultFilter (): (result: RunnitNodeRunResult) => boolean {
			return this.permalinkNodeRun
				? () => true
				: this.featuredResultFeature;
		},
		// One gate for the page body. Both loads are started together in created();
		// the gallery must not mount until the team-eligibility answer is in, or it
		// resolves its stored scope against a filter list that is still growing.
		loadingLibrary (): boolean {
			return this.loadingFeaturedNodeRuns || this.loadingLibraryTeamOptions;
		},
		appliedFilterCount (): number {
			return countAppliedLibraryFilters(this.appliedFilters);
		},
		// Teams whose generations this caller may delete.
		//
		// The v2 /me answer is per-team, which is the shape the Firestore rule
		// uses, so it is the right BASE. It is not the whole answer: it carries no
		// system-admin override, and it fails closed to an empty list on a bad
		// read. Sourcing delete authority from it alone therefore took delete away
		// from callers who had it, including a team admin whose only crime was a
		// failed /me. So the active Team is unioned in whenever the app's own
		// permission resolution says the caller manages it, and this list can only
		// ever be wider than what the caller had before cross-team eligibility
		// existed, never narrower.
		libraryManageableTeamIds (): string[] {
			const ids: string[] = this.libraryTeamOptions
				.filter((option) => !option.disabled)
				.map((option) => option.value);
			const activeTeamId: string = this.team?.id || '';
			if (activeTeamId && this.hasManageAllGenerationsPermission && !ids.includes(activeTeamId)) {
				return [...ids, activeTeamId];
			}
			return ids;
		},
		// Deliberately NOT gated on permission. Delete is refused per run, at the
		// moment of deleting, by canDeleteNodeRun (which mirrors the Firestore
		// rule) and by Firestore itself, and the mixin reports what it skipped.
		// Pre-disabling the whole button on top of that only ever added ways to
		// lose delete a caller actually had: it took the button away for a
		// selection with no node runs in it at all (every Uploads-tab selection),
		// and it turned one unauthorized result in a selection of your own into a
		// dead button with nothing to say which one was the problem.
		deleteSelectedImagesDisabled (): boolean {
			return !this.selectedImagesCount ||
				this.deletingBulkImages ||
				this.downloadingBulkImages ||
				// Not permission: the selection is still being assembled.
				this.selectingAllResults;
		},
	},
	watch: {
		// Result permalink: /runnit/library?nodeRunId=<id>&resultUuid=<uuid>
		// (Global Search primary destination + shareable generation links).
		// `immediate` covers arriving on the URL; non-immediate firings cover
		// same-route pushes (e.g. clicking a search result while already here).
		// Stripping the params on close re-fires with empty → no-op.
		//
		// Watches the PAIR, not nodeRunId alone: a run holds several results, so
		// two search hits can differ only in resultUuid. Keyed on the run id, the
		// second click would leave the carousel showing the first result while
		// the URL named the second.
		permalinkTarget: {
			immediate: true,
			handler (target: string) {
				const parsed = parsePermalinkTargetKey(target);
				if (!parsed) return;
				this.openPermalinkedResult(parsed.nodeRunId, parsed.resultUuid);
			},
		},
		bulkDeleteConfirmDialogOpen: {
			immediate: true,
			async handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal && !newVal && (this.$refs.doubleConfirmDangerDialog as any)) {
					(this.$refs.doubleConfirmDangerDialog as any).reset();
				}
			}
		},
		activeFilterQuery: {
			handler (newQuery: Record<string, string>) {
				// Preserve an active result permalink (?nodeRunId&resultUuid) so a
				// shared/bookmarked URL survives the facet-sync replace — otherwise
				// this watcher (which fires on load as appliedFilters is seeded)
				// would drop the permalink params the moment the carousel opens.
				const q: Record<string, string> = { ...newQuery };
				const routeQuery: Record<string, any> = this.$route.query;
				if (routeQuery.nodeRunId) {
					q.nodeRunId = routeQuery.nodeRunId as string;
					if (routeQuery.resultUuid) q.resultUuid = routeQuery.resultUuid as string;
				}
				if (JSON.stringify(q) !== JSON.stringify(this.$route.query)) {
					this.$router.replace({ query: q }).catch(() => { /* ignore NavigationDuplicated */ });
				}
			},
		},
	},
	async created () {
		const q = this.$route.query;
		// Seed the applied snapshot from the URL (URL filters are live on load).
		// <library-filter-panel> reads this via :initial-applied to seed its draft.
		// Reads every URL-backed field and defaults the rest (owner/board/scope/
		// teamIds), so the snapshot is always complete. Spelling this out by hand is
		// what previously dropped those four keys from the applied default on load.
		this.appliedFilters = readAppliedLibraryFiltersFromQuery(q as Record<string, any>);
		// Internal Admins only until Library Search is released; the release deletes
		// this gate. A non-admin on a shared ?search= link gets the plain Library,
		// and the activeFilterQuery watcher drops the parameter from the URL.
		if (!(this.user && this.user.isAdmin)) {
			this.appliedFilters = { ...this.appliedFilters, search: '' };
		}
		// Auto-expand the filter panel when arriving on a shared/bookmarked URL
		// that already carries filters, so the active controls are visible.
		this.filtersExpanded = this.appliedFilterCount > 0;
		await Promise.all([
			this.initFeaturedNodeRuns(),
			this.loadLibraryTeamOptions(),
		]);
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
		]),
		// --- Result permalink (?nodeRunId=&resultUuid=) --------------------
		// Permission-checked resolve → open the existing carousel natively.
		// Firestore rules produce permission-denied for docs the caller can't
		// read — that lands in the catch and shows the same friendly path as
		// missing/deleted docs (never a raw error).
		async openPermalinkedResult (nodeRunId: string, resultUuid: string): Promise<void> {
			try {
				const snap = await db.doc(`runnitNodeRuns/${nodeRunId}`).get();
				const data: RunnitNodeRun | null = snap.exists ? snap.data() as RunnitNodeRun : null;
				if (!data || data.deletedAt) {
					this.handlePermalinkNotFound();
					return;
				}

				const liveResults: RunnitNodeRunResult[] = (data.results || [])
					.filter((r: RunnitNodeRunResult) => !r.deletedAt);
				const nodeRunResult: RunnitNodeRunResult =
					liveResults.find((r: RunnitNodeRunResult) => r.uuid === resultUuid)
					|| liveResults[0]
					|| null;
				if (!nodeRunResult) {
					this.handlePermalinkNotFound();
					return;
				}

				const nodeRun: RunnitNodeRun = await this.hydrateNodeRun(snap.id, data);
				this.permalinkNodeRun = nodeRun;
				this.imageInfoCarouselConfig = {
					dialogOpen: true,
					nodeRun,
					nodeRunResult,
				};
			} catch (error) {
				console.error('Failed to open the shared generation', error);
				this.handlePermalinkNotFound();
			}
		},
		handlePermalinkNotFound (): void {
			this.updateSnackbar({
				status: SNACKBAR_STATUS.WARN,
				message: 'This generation is no longer available, or you don\'t have access to it.',
				show: true,
			});
			this.stripPermalinkParams();
		},
		// Replace the URL query with the canonical filter query (i.e. drop the
		// permalink params) — keeps back/forward and bookmarks clean.
		stripPermalinkParams (): void {
			this.$router.replace({ query: this.activeFilterQuery }).catch(() => { /* ignore NavigationDuplicated */ });
		},
		onCarouselDialogInput (open: boolean): void {
			this.imageInfoCarouselConfig.dialogOpen = open;
			if (!open && this.permalinkNodeRun) {
				this.permalinkNodeRun = null;
				this.stripPermalinkParams();
			}
		},
		// -------------------------------------------------------------------
		async loadLibraryTeamOptions (): Promise<void> {
			try {
				this.loadingLibraryTeamOptions = true;
				const currentUser = auth.currentUser;
				if (!currentUser) return;
				const idToken = await currentUser.getIdToken();
				this.libraryTeamOptions = await fetchLibraryTeamOptions({
					baseUrl: LIBRARY_API_BASE_URL,
					idToken,
				});
			} catch {
				// Fail closed and silently. Team membership alone is not evidence
				// that the caller may view other users' generations there.
				this.libraryTeamOptions = [];
			} finally {
				this.loadingLibraryTeamOptions = false;
			}
		},
		// The filter panel committed new filters — adopt them (drives the gallery
		// + URL sync via the activeFilterQuery watcher).
		onFiltersApply (applied: AppliedLibraryFilters) {
			if (this.isRunnitBulkActionMenuOpen) {
				this.setLibraryBulkActionMenuOpen(false);
			}
			// The panel builds this from its own draft, which never sees the search
			// box, so keep the box's term rather than let Apply or Clear all wipe it.
			this.appliedFilters = { ...applied, search: this.appliedFilters.search };
		},
		onSearch (search: string) {
			if (this.isRunnitBulkActionMenuOpen) {
				this.setLibraryBulkActionMenuOpen(false);
			}
			this.appliedFilters = { ...this.appliedFilters, search };
		},
		onScopeChange (filter: string) {
			// Lift ALL/FAVORITES/TEAM up from ImageGallery (decision 7) to gate the toolbar.
			this.selectedScope = filter;
		},
		/** The Refresh button: the gallery loads its results again, from the first page. */
		refreshLibrary () {
			const gallery: any = this.$refs.imageGallery;
			if (gallery && gallery.refresh) gallery.refresh();
		},
		toggleFilters () {
			this.filtersExpanded = !this.filtersExpanded;
		},
		// Activate Selection Mode (if not already) and select every result the
		// active filter is currently showing. Selection lives in the gallery, so
		// we enter the bulk-action selection state here, then let the gallery add
		// all of its loaded results to it on the next tick.
		async selectAllFiltered () {
			if (!this.isRunnitBulkActionMenuOpen) {
				this.setLibraryBulkActionMenuOpen(true);
			}
			await this.$nextTick();
			const gallery: any = this.$refs.imageGallery;
			this.selectingAllResults = true;
			try {
				const outcome = gallery && typeof gallery.selectAllShownResults === 'function'
					? await gallery.selectAllShownResults()
					: { status: 'failed' };
				if (outcome.status !== 'success' && this.isRunnitBulkActionMenuOpen) {
					this.setLibraryBulkActionMenuOpen(false);
				}
				if (outcome.status === 'failed') {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Could not select all filtered results. Please try again.',
						show: true,
					});
				} else if (outcome.status === 'success' && outcome.truncated) {
					// Never truncate silently. A count that does not mean what the user
					// thinks it means is the bug this whole packet started from.
					this.updateSnackbar({
						status: SNACKBAR_STATUS.WARN,
						message: `Selected the first ${outcome.selectedCount} results. More match this filter, so narrow it to reach the rest.`,
						show: true,
						timeout: 8000,
					});
				}
			} finally {
				this.selectingAllResults = false;
			}
		},
		setLibraryBulkActionMenuOpen (isOpen: boolean): void {
			if (isOpen) {
				this.enterSelectionMode(
					NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE,
					this.RUNNIT_BULK_ACTIONS_TRIGGER_ID,
					null,
					LIBRARY_BULK_RESULT_TYPES,
				);
				return;
			}
			const gallery: any = this.$refs.imageGallery;
			if (gallery && typeof gallery.cancelSelectAllShownResults === 'function') {
				gallery.cancelSelectAllShownResults();
			}
			this.exitSelectionMode(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
			this.clearSelectionsFromState(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
		},
		canDeleteNodeRun (nodeRun: RunnitNodeRun): boolean {
			return canDeleteLibraryNodeRuns(
				[nodeRun],
				this.user?.id || '',
				this.libraryManageableTeamIds,
				!!this.user?.isAdmin,
			);
		},
		async initFeaturedNodeRuns () {
			try {
				this.loadingFeaturedNodeRuns = true;
				this.featuredNodeRuns = [];

				const nodeRunsRef = db.collection('runnitNodeRuns')
					.where('featured', '==', true)
					.limit(100);

				(await nodeRunsRef.get()).forEach(async (doc) => {
					this.featuredNodeRuns.push(await this.hydrateNodeRun(doc.id, doc.data() as RunnitNodeRun));
					this.featuredNodeRuns = _shuffle(this.featuredNodeRuns);
				});
			} catch (error) {
				console.error('Failed to fetch featured node runs', error);
			} finally {
				this.loadingFeaturedNodeRuns = false;
			}
		},
		// A run's nodeDef snapshot can lack its title; when it does, fetch the
		// generating tool. Shared by the featured runs and the permalinked run.
		async hydrateNodeRun (id: string, data: RunnitNodeRun): Promise<RunnitNodeRun> {
			let fetchedNodeDef: RunnitNodeDef | null = null;
			if (data.nodeDef && !data.nodeDef.title && data.nodeDefId) {
				try {
					const nodeDef = (await db.doc(`runnitNodeDefs/${data.nodeDefId}`).get()).data();
					fetchedNodeDef = { ...nodeDef, id: data.nodeDefId } as RunnitNodeDef;
				} catch (err) {
					console.error('Failed to fetch the generating tool', err);
				}
			}
			return {
				...data,
				...(fetchedNodeDef ? { nodeDef: fetchedNodeDef } : {}),
				get id () { return id; },
			};
		},
		featuredResultFeature (result: RunnitNodeRunResult): boolean {
			return result.featured;
		},
		handleIsPublicUpdated (payload: { nodeRunResult: RunnitNodeRunResult, nodeRun: RunnitNodeRun }): void {
			this.imageInfoCarouselConfig.nodeRun = { ...payload.nodeRun };
			this.imageInfoCarouselConfig.nodeRunResult = { ...payload.nodeRunResult };
			if (this.permalinkNodeRun && this.permalinkNodeRun.id === payload.nodeRun.id) {
				this.permalinkNodeRun = { ...payload.nodeRun };
			}
		},
		handleNavTabChange (tabId: string): void {
			this.currentNavTab = tabId;
		},
		onSelectImageMode (imageMode: string) {
			this.updateRunnitState({
				selectedImageMode: imageMode,
			});
			setRunnitsSelectedImageModeVal(imageMode);
		},
		onSelectColumns (selectedColumns: number) {
			this.updateRunnitState({
				selectedColumns: selectedColumns,
			});
			setRunnitsSelectedColumnsVal(selectedColumns);
		},
	},
	components: {
		ImageGallery,
		LibraryFilterPanel,
		LibrarySearchField,
		BaseButton,
		LoadingState,
		EmptyState,
		RunnitImageInfoCarouselDialog,
		DoubleConfirmDangerDialog,
		GreyButton,
		GlassButton,
		RefreshListButton,
		BaseGreyToggleButton,
		MenuOrBottomSheet,
		SliderSelect,
	},
});
